/**
 * This component supports 3 display modes:
 * - button: a button w/ a small popover that shows the current usage (SM size)
 * - inline: a small inline display that shows the current usage and a progress bar (MD size)
 * - detailed: a detailed view that shows the current usage, quota, and a progress bar (LG size)
 */

import { Component, Input, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { CreditUsage, CreditUsageService } from './credit-usage-service.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { AlertType } from '../alert/alert.component';
import { HostnameService } from 'src/app/hostname-router-service';

@Component({
    selector: 'app-credit-usage-indicator',
    templateUrl: './credit-usage-indicator.component.html',
    styleUrls: ['./credit-usage-indicator.component.scss'],
    standalone: false
})
export class CreditUsageIndicatorComponent {
  @Input() hideText: boolean = false;
  @Input() hideProgressBar: boolean = false;
  @Input() transparentBackground: boolean = false;
  @Input() displayMode: 'button' | 'inline' | 'detailed' = 'button';
  @Input() pendingRecords: number = 0;

  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

  orgUsagePolling: Subscription;
  creditUsage: CreditUsage | null = null;
  isDetailCollapsed: boolean = false; // for detailed mode
  protected AlertType = AlertType;

  get pendingPercentage(): number {
    if (!this.pendingRecords || !this.creditUsage?.peopleQuota) return 0;
    const usagePct = Math.min(this.creditUsage.peopleUsagePercentage || 0, 100);
    return Math.max(
      0,
      Math.min(
        (this.pendingRecords / this.creditUsage.peopleQuota) * 100,
        100 - usagePct
      )
    );
  }

  get clampedUsagePercentage(): number {
    return Math.min(Math.max(this.creditUsage?.peopleUsagePercentage || 0, 0), 100);
  }

  get wouldExceedQuota(): boolean {
    if (!this.creditUsage?.peopleQuota) return false;
    return (this.creditUsage.peopleUsagePercentage || 0) + this.pendingPercentage >= 100;
  }

  constructor(
    private router: Router,
    private sseService: CreditUsageService,
    private authService: AuthService
  ) {
    this.orgUsagePolling = this.sseService.creditUsage$.subscribe((creditUsage) => {
      this.creditUsage = creditUsage;
    });
  }

  navigateToSettings() {
    const orgId = this.authService.getSelectedOrgIdValue;
    this.router.navigate([`/${orgId}/settings`]);
  }

  goToBilling() {
    if (HostnameService.isWorkforceHostname()) {
      this.router.navigate(['/billing']);
    } else {
      this.router.navigate([`/${this.authService.getSelectedOrgIdValue}/settings`]);
    }
  }

  // by default, popover (mat-menu) closes on 'Enter' key, but let's add additional support for escape key so user isn't stuck in the menu
  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.menuTrigger.menuOpen) {
      this.closeMenu();
    }
  }

  closeMenu() {
    if (this.menuTrigger && this.menuTrigger.menuOpen) {
      this.menuTrigger.closeMenu();
    }
  }

  // toggle collapse state for detailed mode
  toggleDetailCollapse() {
    this.isDetailCollapsed = !this.isDetailCollapsed;
  }

  ngOnDestroy() {
    this.orgUsagePolling.unsubscribe();
  }
}
